/**
 * Admin Cats Tab - Manage cat listings
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Cat, Plus, Edit, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminCatsTab = () => {
  const { cats, loadData } = useAdmin();
  
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({
    name: "", age: "", breed: "", gender: "Unknown", description: "", 
    personality: "", is_adopted: false
  });
  const [catFormImage, setCatFormImage] = useState(null);
  const [savingCat, setSavingCat] = useState(false);
  const [uploadingCatImage, setUploadingCatImage] = useState(null);
  const [catImageFile, setCatImageFile] = useState(null);
  
  // Reserve dialog state
  const [showReserveDialog, setShowReserveDialog] = useState(false);
  const [reservingCat, setReservingCat] = useState(null);
  const [reserveForm, setReserveForm] = useState({ name: "", email: "" });

  const resetCatForm = () => {
    setCatForm({ name: "", age: "", breed: "", gender: "Unknown", description: "", personality: "", is_adopted: false });
    setCatFormImage(null);
    setEditingCat(null);
    setShowCatForm(false);
  };

  const handleEditCat = (cat) => {
    setEditingCat(cat);
    setCatForm({
      name: cat.name || "",
      age: cat.age || "",
      breed: cat.breed || "",
      gender: cat.gender || "Unknown",
      description: cat.description || "",
      personality: Array.isArray(cat.personality) ? cat.personality.join(", ") : (cat.personality || ""),
      is_adopted: cat.is_adopted || false
    });
    setCatFormImage(null);
    setShowCatForm(true);
  };

  const handleSaveCat = async () => {
    if (!catForm.name || !catForm.breed || !catForm.age) {
      toast.error("Please fill in name, breed, and age");
      return;
    }
    setSavingCat(true);
    try {
      const catData = {
        ...catForm,
        personality: catForm.personality.split(",").map(p => p.trim()).filter(p => p)
      };
      
      let savedCatId;
      if (editingCat) {
        await axios.put(`${API}/cats/${editingCat.cat_id}`, catData, { withCredentials: true });
        savedCatId = editingCat.cat_id;
        toast.success("Cat updated successfully");
      } else {
        const response = await axios.post(`${API}/cats`, catData, { withCredentials: true });
        savedCatId = response.data.cat_id;
        toast.success("Cat added successfully");
      }
      
      if (catFormImage && savedCatId) {
        const formData = new FormData();
        formData.append("file", catFormImage);
        await axios.post(`${API}/cats/${savedCatId}/upload-image`, formData, { 
          withCredentials: true 
        });
        toast.success("Image uploaded!");
      }
      
      resetCatForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save cat");
    } finally {
      setSavingCat(false);
    }
  };

  const handleDeleteCat = async (catId) => {
    if (!window.confirm("Are you sure you want to delete this cat?")) return;
    try {
      await axios.delete(`${API}/cats/${catId}`, { withCredentials: true });
      toast.success("Cat deleted");
      loadData();
    } catch {
      toast.error("Failed to delete cat");
    }
  };

  const handleToggleAdopted = async (cat) => {
    try {
      await axios.put(`${API}/cats/${cat.cat_id}`, { ...cat, is_adopted: !cat.is_adopted }, { withCredentials: true });
      toast.success(cat.is_adopted ? "Cat marked as available" : "Cat marked as adopted");
      loadData();
    } catch {
      toast.error("Failed to update cat status");
    }
  };

  const handleUploadCatImage = async (catId) => {
    if (!catImageFile) {
      toast.error("Please select an image");
      return;
    }
    setUploadingCatImage(catId);
    try {
      const formData = new FormData();
      formData.append("file", catImageFile);
      await axios.post(`${API}/cats/${catId}/upload-image`, formData, {
        withCredentials: true
      });
      toast.success("Cat image uploaded");
      setCatImageFile(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploadingCatImage(null);
    }
  };

  const handleToggleReserved = async (cat) => {
    if (!cat.is_reserved) {
      setReservingCat(cat);
      setReserveForm({ name: "", email: "" });
      setShowReserveDialog(true);
    } else {
      try {
        await axios.put(`${API}/cats/${cat.cat_id}/reserve`, { 
          is_reserved: false, 
          reserved_by: "",
          reserved_email: ""
        }, { withCredentials: true });
        toast.success("Cat unreserved");
        loadData();
      } catch {
        toast.error("Failed to update cat");
      }
    }
  };

  const handleConfirmReserve = async () => {
    if (!reservingCat) return;
    try {
      await axios.put(`${API}/cats/${reservingCat.cat_id}/reserve`, { 
        is_reserved: true, 
        reserved_by: reserveForm.name || "",
        reserved_email: reserveForm.email || ""
      }, { withCredentials: true });
      toast.success("Cat marked as reserved" + (reserveForm.email ? " - Email notification sent!" : ""));
      setShowReserveDialog(false);
      setReservingCat(null);
      loadData();
    } catch {
      toast.error("Failed to reserve cat");
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Manage Cats</CardTitle>
            <CardDescription>Add, edit, or remove cats from the adoption listings</CardDescription>
          </div>
          <Button 
            onClick={() => { resetCatForm(); setShowCatForm(true); }}
            className="bg-amber-600 hover:bg-amber-700 rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Cat
          </Button>
        </CardHeader>
        <CardContent>
          {/* Cat Form Modal */}
          {showCatForm && (
            <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h3 className="font-semibold text-stone-900 mb-4">{editingCat ? "Edit Cat" : "Add New Cat"}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Name *</label>
                  <Input 
                    value={catForm.name} 
                    onChange={e => setCatForm({...catForm, name: e.target.value})}
                    placeholder="e.g., Whiskers"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Breed *</label>
                  <Input 
                    value={catForm.breed} 
                    onChange={e => setCatForm({...catForm, breed: e.target.value})}
                    placeholder="e.g., British Shorthair"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Age *</label>
                  <Input 
                    value={catForm.age} 
                    onChange={e => setCatForm({...catForm, age: e.target.value})}
                    placeholder="e.g., 2 years"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Gender</label>
                  <select 
                    value={catForm.gender}
                    onChange={e => setCatForm({...catForm, gender: e.target.value})}
                    className="w-full border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Unknown">Unknown</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-stone-700 mb-1">Description</label>
                  <textarea 
                    value={catForm.description}
                    onChange={e => setCatForm({...catForm, description: e.target.value})}
                    className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px]"
                    placeholder="Tell us about this cat's story and personality..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-stone-700 mb-1">Personality Traits (comma-separated)</label>
                  <Input 
                    value={catForm.personality}
                    onChange={e => setCatForm({...catForm, personality: e.target.value})}
                    placeholder="e.g., Playful, Affectionate, Good with children"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Cat Photo {!editingCat && <span className="text-amber-600">*</span>}
                  </label>
                  <Input 
                    type="file"
                    accept="image/*"
                    onChange={e => setCatFormImage(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                  />
                  {catFormImage && (
                    <p className="text-xs text-green-600 mt-1">✓ {catFormImage.name} selected</p>
                  )}
                  {editingCat && !catFormImage && (
                    <p className="text-xs text-stone-500 mt-1">Leave empty to keep current image</p>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="is_adopted"
                      checked={catForm.is_adopted}
                      onChange={e => setCatForm({...catForm, is_adopted: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <label htmlFor="is_adopted" className="text-sm text-stone-700">Already adopted</label>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button 
                  onClick={handleSaveCat} 
                  disabled={savingCat}
                  className="bg-amber-600 hover:bg-amber-700 rounded-full"
                >
                  {savingCat ? "Saving..." : (editingCat ? "Update Cat" : "Add Cat")}
                </Button>
                <Button variant="outline" onClick={resetCatForm} className="rounded-full">Cancel</Button>
              </div>
            </div>
          )}

          {/* Cats Grid */}
          {cats.length === 0 ? (
            <p className="text-center text-stone-500 py-8">No cats added yet. Click "Add Cat" to get started.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cats.map(cat => (
                <Card key={cat.cat_id} className="overflow-hidden">
                  <div className="aspect-square relative bg-stone-100">
                    {(cat.image_base64 || cat.image_url) ? (
                      <img 
                        src={cat.image_base64 ? `data:${cat.image_mime_type || 'image/jpeg'};base64,${cat.image_base64}` : cat.image_url} 
                        alt={cat.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Cat className="w-16 h-16 text-stone-300" />
                      </div>
                    )}
                    {cat.is_reserved && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="absolute inset-0 bg-black/40" />
                        <div className="relative bg-red-600 text-white px-6 py-2 font-bold text-lg transform -rotate-12 shadow-lg">
                          RESERVED
                        </div>
                      </div>
                    )}
                    {cat.is_adopted && (
                      <div className="absolute top-2 right-2">
                        <Badge className="bg-green-600 text-white">Adopted</Badge>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2">
                      <div className="flex items-center gap-2">
                        <Input 
                          type="file" 
                          accept="image/*"
                          onChange={e => setCatImageFile(e.target.files?.[0])}
                          className="text-xs text-white file:bg-amber-600 file:text-white file:border-0 file:rounded file:text-xs"
                        />
                        <Button 
                          size="sm"
                          onClick={() => handleUploadCatImage(cat.cat_id)}
                          disabled={uploadingCatImage === cat.cat_id || !catImageFile}
                          className="bg-amber-600 hover:bg-amber-700 text-xs px-2"
                        >
                          {uploadingCatImage === cat.cat_id ? "..." : "Upload"}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-bold text-lg">{cat.name}</h3>
                        <p className="text-sm text-stone-500">{cat.breed} • {cat.age} • {cat.gender}</p>
                      </div>
                    </div>
                    {cat.is_reserved && cat.reserved_by && (
                      <p className="text-xs text-red-600 mb-2">Reserved by: {cat.reserved_by}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge 
                        className={`cursor-pointer ${cat.is_adopted ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
                        onClick={() => handleToggleAdopted(cat)}
                      >
                        {cat.is_adopted ? "Adopted" : "Available"}
                      </Badge>
                      <Badge 
                        className={`cursor-pointer ${cat.is_reserved ? "bg-red-100 text-red-700" : "bg-stone-100 text-stone-600"}`}
                        onClick={() => handleToggleReserved(cat)}
                      >
                        {cat.is_reserved ? "Reserved" : "Not Reserved"}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEditCat(cat)} className="flex-1">
                        <Edit className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteCat(cat.cat_id)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reserve Dialog */}
      <Dialog open={showReserveDialog} onOpenChange={setShowReserveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reserve {reservingCat?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">Customer Name</label>
              <Input 
                value={reserveForm.name}
                onChange={e => setReserveForm({...reserveForm, name: e.target.value})}
                placeholder="Enter customer name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Customer Email (for notification)</label>
              <Input 
                type="email"
                value={reserveForm.email}
                onChange={e => setReserveForm({...reserveForm, email: e.target.value})}
                placeholder="Enter customer email"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReserveDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmReserve} className="bg-amber-600 hover:bg-amber-700">
              Confirm Reservation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminCatsTab;
